// «Траектории по HSI» («живая вода»): усики обратной адвекции HSI-поля, окрашенные
// HSI в точке «сейчас» (порт правой панели eln lagrangian_hsi.py). Здесь — только
// стиль слоя (шкала, ширины, id слоёв). Геометрию усиков считает бэкенд,
// нарезку по дню — браузер (models/hsiDriftDay.ts).

import type { LayerPalette } from '../api'
import { mapColorOf } from '../brand/mapColors'
import { paletteGradient } from '../maps/colormaps'
import { formatLegendValue } from '../maps/legendFormat'
import { tokenColor } from './cssToken'
import { OVERLAY_PREFIX } from './layerOrder'

export const HSI_DRIFT_PREFIX = OVERLAY_PREFIX + 'hsidrift-'

// Шкала усика — ТА ЖЕ, что красит растр HSI: палитра слоя `probability` из
// каталога сервера, опоры 0…100 (`src/palettes/registry.py`).
//
// До 16.08.2026 усик красился своей гаммой (inferno) и своей нормировкой (p98
// поля, «ниже индекса 86»). Он лежит ПОВЕРХ того самого растра, и одно и то же
// число выглядело двумя разными цветами: 60 на усике читалось как 70 на карте.
// Шкала HSI при этом смысловая (`SEMANTIC_LAYERS`) — бренд её не перекрывает
// ни на тайле, ни здесь: «зелёное — идти туда» обязано значить одно и то же.

/** CSS-градиент шкалы для свотча легенды — из палитры слоя HSI. */
export function driftGradient(palette: LayerPalette | undefined): string {
  return paletteGradient(palette)
}

/** Подписи краёв шкалы усиков — теми же числами и тем же форматом, что у
 *  легенды слоя. Палитры нет — краёв нет: цифру не выдумываем. */
export function driftScaleEdges(palette: LayerPalette | undefined): [string, string] | null {
  if (!palette || palette.stops.length < 2) return null
  const lo = palette.stops[0]
  const hi = palette.stops[palette.stops.length - 1]
  const span = hi - lo
  return [formatLegendValue(lo, span), formatLegendValue(hi, span)]
}

export type HsiDriftPart = 'line' | 'origin' | 'arrow' | 'hover'

export function hsiDriftSourceId(key: string, part: HsiDriftPart): string {
  // Ореол подсветки рисует ТУ ЖЕ геометрию, что и линия, только шире — своего
  // источника ему не нужно, и заводить его значило бы держать вторую копию
  // сотен усиков в памяти карты.
  const src = part === 'hover' ? 'line' : part
  return `${HSI_DRIFT_PREFIX}src-${key}-${src}`
}
export function hsiDriftLayerId(key: string, part: HsiDriftPart): string {
  return `${HSI_DRIFT_PREFIX}${key}-${part}`
}

function _rgb(c: readonly number[]): string {
  return `rgb(${c[0]},${c[1]},${c[2]})`
}

/** Цвет по HSI — по опорам палитры растра, в АБСОЛЮТНЫХ единицах индекса.
 *
 *  Нормировки на p98 здесь нет намеренно: усик обязан совпадать с пятном под
 *  ним, а растянутая шкала показывала бы 60 цветом семидесяти. Значения за
 *  краями шкалы прижимаются к крайнему цвету (interpolate зажимает края).
 *
 *  Каталог ещё не приехал — возвращается токен акцента, а не зашитая шкала: тот
 *  же приём, что у изобат (`isobathRampColorExpr`). */
export function hsiColorExpr(palette: LayerPalette | undefined): unknown {
  if (!palette || palette.colors.length < 2) return tokenColor('--accent')
  return [
    'interpolate',
    ['linear'],
    ['coalesce', ['get', 'hsi'], 0],
    ...palette.stops.flatMap((s, i) => [s, _rgb(palette.colors[i])]),
  ]
}

/** Ширина усика по зуму: тонко на обзорных (усиков много, сливаются), номинал на
 *  рабочих, чуть заметнее на предельных. */
export function hsiLineWidthExpr() {
  return ['interpolate', ['linear'], ['zoom'], 3, 0.7, 7, 1.3, 12, 2.2]
}

// --- Подсветка усика под курсором -------------------------------------------
// Усиков в кадре сотни, они пересекаются и сливаются: глазом проследить один
// путь от origin до «сейчас» не выходит. Наведение подсвечивает ОДИН усик
// целиком — белым ореолом ПОД цветной линией.
//
// Почему ореолом, а не утолщением самой линии: линия красится шкалой HSI, и на
// жёлтом конце шкалы поверх жёлтого растра HSI утолщение не читается вовсе.
// Белый здесь — приём читаемости поверх чужой картинки, как обводки подписей и
// шевроны направления, а не айдентика: бренд его не перекрывает.

/** Порядок слоёв усиков СНИЗУ ВВЕРХ — в нём они и добавляются на карту.
 *
 *  Ореол обязан лежать под линией: наверху он закрасил бы сам усик, то есть
 *  ровно тот цвет по шкале HSI, ради которого усик на карте. */
export function hsiDriftLayerOrder(): HsiDriftPart[] {
  return ['hover', 'line', 'arrow', 'origin']
}

/** Половина стороны квадрата опроса под курсором, px. Точкой в усик не попасть:
 *  на обзорных зумах он шириной 0,7 px. */
export const HSI_HOVER_HIT_PX = 5

/** Ширина ореола по зуму — кратно шире линии на КАЖДОМ зуме.
 *
 *  Своим `interpolate`, а не множителем поверх `hsiLineWidthExpr()`: движок
 *  требует, чтобы zoom-интерполяция была самым внешним выражением, и
 *  `['*', <interpolate по зуму>, 3]` он отвергает целиком (та же грабля, что у
 *  казинга изобат). */
export function hsiHoverWidthExpr() {
  return ['interpolate', ['linear'], ['zoom'], 3, 2.6, 7, 4.2, 12, 6.6]
}

/** Краска ореола. Полупрозрачный белый: сплошной резал бы глаз на тёмном
 *  растре, а сквозь него видно, что под ореолом лежит. */
export function hsiHoverPaint(): Record<string, unknown> {
  return {
    'line-color': '#ffffff',
    'line-width': hsiHoverWidthExpr(),
    'line-opacity': 0.75,
  }
}

/** Фильтр слоя-ореола: подсвечен ровно один усик — тот, чей `tid` под курсором.
 *
 *  `null` (курсор ни на чём) даёт фильтр, не пропускающий НИЧЕГО: слой при этом
 *  остаётся на карте пустым — снимать и добавлять его на каждое движение мыши
 *  дороже и заставило бы перестраивать порядок слоёв. */
export function hsiHoverFilter(tid: string | null): unknown {
  if (tid == null) return ['literal', false]
  return ['==', ['get', 'tid'], tid]
}

/** Радиус кружка-origin по зуму (массив ненулевых HSI-точек 7 дн назад).
 *
 *  Мельче, чем был у залитой точки: полому кружку заметность даёт обводка, а
 *  крупное кольцо на старом конце снова читалось бы как голова. */
export function hsiOriginRadiusExpr() {
  return ['interpolate', ['linear'], ['zoom'], 3, 1.3, 7, 2.4, 12, 3.8]
}

/** Заливка кружка-origin — ПУСТАЯ, цвет уходит в обводку.
 *
 *  Залитая точка стояла на конце «7 суток назад», и усик читался как стрелка,
 *  направленная против течения: жирный конец глаз принимает за остриё (фидбек
 *  владельца 2026-08-07). Полое кольцо читается как начало пути. */
export function hsiOriginPaint(color: unknown) {
  return {
    'circle-radius': hsiOriginRadiusExpr(),
    'circle-color': 'transparent',
    'circle-opacity': 0,
    'circle-stroke-color': color,
    'circle-stroke-width': 1.1,
    'circle-stroke-opacity': 0.9,
  } as Record<string, unknown>
}

// --- Направление усика ------------------------------------------------------
// Куда течёт вода, должно быть видно без анимации. Шевроны ставим ВДОЛЬ линии:
// при `symbol-placement: 'line'` MapLibre доворачивает иконку по касательной,
// совмещая её ось x с направлением ломаной, а вершины усика приходят с бэкенда
// в порядке origin → «сейчас» (пинится тестами `tests/test_cabinet_hsi_drift.py`).
// Тот же приём уже носит трек сноса косяка — карты не должны говорить о
// направлении двумя разными языками.

/** Размер шеврона направления, px. */
export const HSI_ARROW_PX = 9

/** Шаг шевронов вдоль усика, px. Реже, чем на треке (72): усиков в кадре сотни,
 *  и частые шевроны сливаются в сплошную линию из стрелок. */
export const HSI_ARROW_SPACING_PX = 58

/** id картинки шеврона (рисуется в canvas в MapCanvas). */
export const HSI_ARROW_IMAGE_ID = 'hsi-drift-arrow'

/** Одноцветный шеврон поверх шкалы усика: у нас белый, потому что усик идёт от
 *  почти чёрного до жёлтого и шеврон обязан читаться на обоих концах. */
export function hsiArrowColor(): string {
  return mapColorOf('hsi-arrow')
}

export function hsiArrowLayout(): Record<string, unknown> {
  return {
    'icon-image': HSI_ARROW_IMAGE_ID,
    'symbol-placement': 'line',
    'symbol-spacing': HSI_ARROW_SPACING_PX,
    // Без этого MapLibre выбрасывает шевроны, столкнувшиеся со стрелками
    // течений и маркерами, — на пёстрой карте от направления не осталось бы
    // ничего (та же причина, что у стрелок трека).
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-rotation-alignment': 'map',
  }
}
